import { makeid } from 'utils/helpers/makeid';

import {
  nurseContactWithPatientItem,
  nurseDecisionMakingNextStepsItem,
  nurseDigitalEngagementEducationMethodItem,
  nurseEmergencyCareAdvisedItem,
  nurseFollowUpPlanItem,
  nurseMaTasksDelegatedItem,
  nurseNoCallMadeRationaleItem,
  nursePatientAcknowledgementItem,
  nursePatientAgreeabilityItem,
  nurseReviewEscalationProtocolItem,
} from '../common';

import type { TFormItem } from '../../types';

export const bpClinicalAssessmentList = [
  { title: 'Severe headache', name: 'severeHeadache' },
  { title: 'Chest pain/pressure', name: 'chestPainPressure' },
  {
    title: 'New/worsening shortness of breath',
    name: 'newWorseningShortnessOfBreath',
  },
  {
    title: 'Confusion/altered mental status',
    name: 'confusionAlteredMentalStatus',
  },
  { title: 'Vision changes', name: 'visionChanges' },
  {
    title: 'Severe dizziness, loss of balance, syncope',
    name: 'severeDizzinessLossOfBalanceSyncope',
  },
  { title: 'New limb weakness/numbness', name: 'newLimbWeaknessNumbness' },
  { title: 'Palpitations', name: 'palpitations' },
  { title: 'Dizziness, lightheadedness', name: 'dizzinessLightheadedness' },
  { title: 'Fatigue or weakness', name: 'fatigueWeakness' },
  { title: 'Swelling in legs/feet', name: 'swellingInLegsFeet' },
  { title: 'Nausea/vomiting', name: 'nauseaVomiting' },
  {
    title: 'Difficulty urinating/unusual urine',
    name: 'difficultyUrinatingUnusualUrine',
  },
  {
    title: 'Adherence to BP meds as prescribed',
    name: 'adherenceToBPMedAsPrescribed',
  },
  { title: 'Recent med/health changes', name: 'recentMedHealthChanges' },
];

const clinicalAssessmentItems: TFormItem[] = bpClinicalAssessmentList.map(
  (item) => {
    return {
      key: makeid(10),
      title: <p>{item.title}:</p>,
      type: 'radio',
      name: item.name,
      required: false,
      options: [
        { label: 'Present', value: true, key: makeid(10) },
        { label: 'Denied', value: false, key: makeid(10) },
      ],
    };
  },
);

export const bloodPressure: TFormItem[] = [
  {
    key: makeid(10),
    title: <p>Chart Review: At-risk trend summary:</p>,
    type: 'empty',
    name: '_empty',
    items: [
      {
        key: makeid(10),
        title: (
          <p>
            Highest systolic: <span>__ mmHg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestSystolic',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest systolic: <span>__ mmHg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestSystolic',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Highest diastolic: <span>__ mmHg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestDiastolic',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest diastolic: <span>__ mmHg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestDiastolic',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Highest pulse: <span>__ bpm</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestPulse',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest pulse: <span>__ bpm</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestPulse',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: <p>Readings are trending:</p>,
        type: 'radio',
        required: false,
        name: 'typeofTrend',
        options: [
          {
            label: 'High',
            value: 'high',
            key: makeid(10),
          },
          {
            label: 'Low',
            value: 'low',
            key: makeid(10),
          },
          {
            label: 'Both',
            value: 'high&low',
            key: makeid(10),
          },
          {
            label: 'Fluctuating',
            value: 'fluctuating',
            key: makeid(10),
          },
        ],
      },
      {
        key: makeid(10),
        title: null,
        name: 'typeofTrendComment',
        type: 'textarea',
      },
    ],
  },
  { ...nurseContactWithPatientItem },
  { ...nurseNoCallMadeRationaleItem },
  {
    key: makeid(10),
    title: <p>Clinical Assessment</p>,
    type: 'empty',
    name: '_empty',
    items: [
      ...clinicalAssessmentItems,
      {
        key: makeid(10),
        title: null,
        type: 'textarea',
        name: 'clinicalAssessmentComment',
      },
    ],
  },
  { ...nurseReviewEscalationProtocolItem },
  { ...nurseEmergencyCareAdvisedItem },
  {
    key: makeid(10),
    title: <p>Potential contributing factors:</p>,
    type: 'checkbox',
    required: false,
    name: 'potentialContributingFactors',
    options: [
      {
        label: 'Missed/skipped medication(s)',
        value: 'missed/skipped medication(s)',
        key: makeid(10),
      },
      { label: 'Non-adherence', value: 'non-adherence', key: makeid(10) },
      { label: 'Diet/fluids', value: 'diet/fluids', key: makeid(10) },
      {
        label: 'Device/measurement issue',
        value: 'device/measurement issue',
        key: makeid(10),
      },
      {
        label: 'Illness/infection',
        value: 'illness/infection',
        key: makeid(10),
      },
      {
        label: 'Recent stress',
        value: 'recent stress',
        key: makeid(10),
      },
      {
        label: 'Physical activity or inactivity',
        value: 'physical activity or inactivity',
        key: makeid(10),
      },
      {
        label: 'No identifiable cause',
        value: 'no identifiable cause',
        key: makeid(10),
      },
    ],
    items: [
      {
        key: makeid(10),
        title: null,
        type: 'textarea',
        name: 'potentialContributingFactorsComment',
      },
    ],
  },
  { ...nurseDecisionMakingNextStepsItem },
  {
    key: makeid(10),
    type: 'checkbox',
    title: <p>Patient education provided:</p>,
    name: 'patientEducationProvided',
    required: false,
    options: [
      {
        label: 'Medication adherence',
        value: 'medication adherence',
        key: makeid(10),
      },
      {
        label: 'Home BP measurement technique',
        value: 'home BP measurement technique',
        key: makeid(10),
      },
      {
        label: 'Diet/salt/fluid',
        value: 'diet/salt/fluid',
        key: makeid(10),
      },
      {
        label: 'Warning signs & when to seek help',
        value: 'warning signs & when to seek help',
        key: makeid(10),
      },
    ],
    items: [
      {
        key: makeid(10),
        title: null,
        type: 'textarea',
        name: 'patientEducationProvidedComment',
      },
    ],
  },
  { ...nurseDigitalEngagementEducationMethodItem },
  { ...nurseMaTasksDelegatedItem },
  {
    key: makeid(10),
    type: 'textarea',
    title: <p>Nurse clinical summary and rationale:</p>,
    name: 'nurseClinicalSummaryAndRationale',
    required: false,
  },
  { ...nurseFollowUpPlanItem },
  { ...nursePatientAcknowledgementItem },
  { ...nursePatientAgreeabilityItem },
];
